import { useCallback, useEffect, useState } from 'react'
import { getLatestAiGrowthPlan, getStudentGrowthMe } from '../../api.js'
import { useAuth } from '../../auth-context.js'
import {
  getLearningTimeline,
  getLearningXp,
} from '../../student-roadmap.js'
import { getEffectiveGrowthPlan } from '../../student-growth-state.js'
import { buildLearningProgress } from './progress.js'

export function useLearningPlan({ autoLoad = true } = {}) {
  const { isLoggedIn } = useAuth()
  const [state, setState] = useState({
    error: '',
    loading: autoLoad,
    payload: null,
    revision: 0,
  })

  const load = useCallback((signal) => {
    if (!isLoggedIn) {
      setState((current) => ({
        ...current,
        error: '',
        loading: false,
        payload: null,
      }))
      return Promise.resolve(null)
    }

    setState((current) => ({ ...current, error: '', loading: true }))
    return Promise.all([
      getLatestAiGrowthPlan(signal).catch(() => ({ plan: null })),
      getStudentGrowthMe(signal).catch(() => null),
    ])
      .then(([aiPayload, growthPayload]) => {
        const payload = aiPayload?.plan?.source === 'ai_generated' ? { plan: aiPayload.plan } : growthPayload
        setState((current) => ({
          ...current,
          error: '',
          loading: false,
          payload,
        }))
        return payload
      })
      .catch((error) => {
        if (error.name === 'AbortError') return null
        setState((current) => ({
          ...current,
          error: error?.message || '学习计划暂时加载失败。',
          loading: false,
          payload: null,
        }))
        return null
      })
  }, [isLoggedIn])

  useEffect(() => {
    if (!autoLoad) return undefined
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [autoLoad, load])

  const plan = getEffectiveGrowthPlan(state.payload)
  const progress = buildLearningProgress(plan)

  const refreshLocalProgress = useCallback(() => {
    setState((current) => ({ ...current, revision: current.revision + 1 }))
  }, [])

  return {
    ...state,
    completedStageIds: progress.completedStageIds,
    currentStage: progress.currentStage,
    currentStep: progress.currentStep,
    isLoggedIn,
    load,
    payload: state.payload,
    plan,
    refreshLocalProgress,
    roadmap: progress.roadmap,
    summary: progress.summary,
    timeline: getLearningTimeline(plan),
    xp: getLearningXp(plan),
  }
}
